<template>
  <v-card v-if="hasAnyData" class="live-card mb-3">
    <div class="live-card__head">
      <span class="live-card__dot" />
      <span class="live-card__title">Live im Shop</span>
      <div class="live-card__chips">
        <button
          v-for="option in shopOptions"
          :key="option.code"
          type="button"
          class="live-card__chip"
          :class="{ 'live-card__chip--active': selected === option.code }"
          @click="selected = option.code"
        >
          {{ option.label }}
          <span v-if="activeByShop[option.code] || option.code === 'ALL'" class="live-card__chip-count">
            {{ option.code === 'ALL' ? totalActive : activeByShop[option.code] }}
          </span>
        </button>
      </div>
      <v-spacer />
      <span class="live-card__hint">aktualisiert sich automatisch</span>
    </div>

    <div class="live-card__body">
      <!-- Besucher jetzt + Verlauf -->
      <div class="live-card__now">
        <div class="live-card__big">{{ shownActive }}</div>
        <div class="live-card__big-label">Besucher jetzt</div>
        <svg v-if="sparkPoints" class="live-card__spark" viewBox="0 0 120 34" preserveAspectRatio="none">
          <polyline :points="sparkPoints" fill="none" stroke="#27a950" stroke-width="2" stroke-linejoin="round" stroke-linecap="round" />
        </svg>
        <div class="live-card__spark-label">Seitenaufrufe · letzte 60 Min</div>
      </div>

      <!-- Funnel heute -->
      <div class="live-card__funnel">
        <!-- Raten-Zeile immer rendern (ggf. leer), damit alle Boxen gleich hoch sind -->
        <div v-for="step in funnel" :key="step.label" class="live-card__step">
          <div class="live-card__step-value">{{ step.value }}</div>
          <div class="live-card__step-label">{{ step.label }}</div>
          <div class="live-card__step-rate">{{ step.rate || " " }}</div>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script>
import {
  collection,
  doc,
  onSnapshot,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import { db } from "../../../fb";

const SHOPS = [
  { code: "AQ_DE", label: "DE" },
  { code: "AQ_FR", label: "FR" },
  { code: "AQ_ES", label: "ES" },
  { code: "OH_US", label: "OhMy" },
];
const ACTIVE_WINDOW_MS = 5 * 60 * 1000; // Ping in den letzten 5 Min = aktiv
const RESUBSCRIBE_MS = 60 * 1000; // zeitbasierte Queries: Grenze jede Minute neu setzen

/** Tages-Schlüssel in Europe/Berlin (identisch zum Backend) */
const berlinDayKey = () =>
  new Date().toLocaleString("sv", { timeZone: "Europe/Berlin" }).slice(0, 10);

export default {
  name: "LiveStatsCard",
  data() {
    return {
      selected: "ALL",
      activeByShop: {},
      minutesByShop: {},
      todayByShop: {},
      unsubs: [],
      timer: null,
    };
  },
  computed: {
    shopOptions() {
      return [{ code: "ALL", label: "Alle" }, ...SHOPS];
    },
    selectedShops() {
      return this.selected === "ALL" ? SHOPS.map((s) => s.code) : [this.selected];
    },
    totalActive() {
      return SHOPS.reduce((sum, s) => sum + (this.activeByShop[s.code] || 0), 0);
    },
    shownActive() {
      return this.selectedShops.reduce((sum, code) => sum + (this.activeByShop[code] || 0), 0);
    },
    hasAnyData() {
      return (
        this.totalActive > 0 ||
        Object.keys(this.todayByShop).length > 0 ||
        Object.values(this.minutesByShop).some((m) => m.length)
      );
    },
    funnel() {
      const sum = (key) =>
        this.selectedShops.reduce((acc, code) => acc + ((this.todayByShop[code] || {})[key] || 0), 0);
      const t = {
        sessions: sum("sessions"),
        carts: sum("carts"),
        checkouts: sum("checkouts"),
        orders: sum("orders"),
      };
      const rate = (a, b) =>
        b > 0 ? `${((a / b) * 100).toFixed(1).replace(".", ",")} %` : null;
      return [
        { label: "Sessions heute", value: t.sessions, rate: null },
        { label: "Warenkörbe", value: t.carts, rate: rate(t.carts, t.sessions) },
        { label: "Checkouts", value: t.checkouts, rate: rate(t.checkouts, t.carts) },
        { label: "Bestellungen", value: t.orders, rate: rate(t.orders, t.sessions) },
      ];
    },
    sparkPoints() {
      // Pageviews je Minute über die ausgewählten Shops summiert
      const byMinute = {};
      let any = false;
      this.selectedShops.forEach((code) => {
        (this.minutesByShop[code] || []).forEach((b) => {
          if (!b.ts) return;
          any = true;
          const key = Math.floor(b.ts.toMillis() / 60000);
          byMinute[key] = (byMinute[key] || 0) + (b.pageviews || 0);
        });
      });
      if (!any) return null;
      const nowMin = Math.floor(Date.now() / 60000);
      const series = [];
      for (let i = 59; i >= 0; i--) series.push(byMinute[nowMin - i] || 0);
      const max = Math.max(...series, 1);
      return series
        .map((v, i) => `${(i / 59) * 120},${32 - (v / max) * 28}`)
        .join(" ");
    },
  },
  mounted() {
    this.subscribe();
    this.timer = setInterval(this.subscribe, RESUBSCRIBE_MS);
  },
  beforeDestroy() {
    clearInterval(this.timer);
    this.unsubscribeAll();
  },
  methods: {
    unsubscribeAll() {
      this.unsubs.forEach((u) => u && u());
      this.unsubs = [];
    },
    subscribe() {
      this.unsubscribeAll();
      const silent = () => {}; // fehlende Rechte/Daten: Karte bleibt einfach leer

      SHOPS.forEach(({ code }) => {
        // Aktive Besucher: Sessions mit lastSeen im 5-Min-Fenster
        this.unsubs.push(
          onSnapshot(
            query(
              collection(db, `liveStats/${code}/sessions`),
              where("lastSeen", ">", new Date(Date.now() - ACTIVE_WINDOW_MS))
            ),
            (snap) => this.$set(this.activeByShop, code, snap.size),
            silent
          )
        );

        // Minuten-Buckets der letzten 60 Min (Sparkline)
        this.unsubs.push(
          onSnapshot(
            query(
              collection(db, `liveStats/${code}/minutes`),
              where("ts", ">", new Date(Date.now() - 60 * 60 * 1000)),
              orderBy("ts")
            ),
            (snap) =>
              this.$set(this.minutesByShop, code, snap.docs.map((d) => d.data())),
            silent
          )
        );

        // Tages-Funnel (heute, Berlin-Zeit)
        this.unsubs.push(
          onSnapshot(
            doc(db, `liveStats/${code}/days/${berlinDayKey()}`),
            (snap) => {
              if (snap.exists()) this.$set(this.todayByShop, code, snap.data());
            },
            silent
          )
        );
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.live-card {
  border-radius: 14px !important;
  padding: 14px 18px;

  &__head {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
    flex-wrap: wrap;
  }

  &__dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: #27a950;
    animation: live-pulse 1.6s ease-in-out infinite;
  }

  &__title {
    font-size: 15px;
    font-weight: 700;
  }

  &__chips {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-wrap: wrap;
  }

  &__chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    background: #fff;
    border-radius: 999px;
    font-size: 11.5px;
    font-weight: 600;
    padding: 2px 10px;
    cursor: pointer;
    color: rgba(0, 0, 0, 0.6);

    &--active {
      background: #1f2937;
      border-color: #1f2937;
      color: #fff;
    }
  }

  &__chip-count {
    font-variant-numeric: tabular-nums;
    opacity: 0.75;
  }

  &__hint {
    font-size: 11px;
    color: rgba(0, 0, 0, 0.35);
  }

  &__body {
    display: flex;
    gap: 28px;
    align-items: stretch;

    @media (max-width: 700px) {
      flex-direction: column;
      gap: 14px;
    }
  }

  &__now {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
  }

  &__big {
    font-size: 34px;
    font-weight: 800;
    line-height: 1.1;
    font-variant-numeric: tabular-nums;
  }

  &__big-label {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.5);
    margin-bottom: 6px;
  }

  &__spark {
    width: 150px;
    height: 34px;
  }

  &__spark-label {
    font-size: 10.5px;
    color: rgba(0, 0, 0, 0.35);
  }

  &__funnel {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  &__step {
    flex: 1;
    min-width: 100px;
    background: #f8f9fb;
    border: 1px solid rgba(0, 0, 0, 0.05);
    border-radius: 10px;
    padding: 10px 12px;
  }

  &__step-value {
    font-size: 20px;
    font-weight: 750;
    font-variant-numeric: tabular-nums;
  }

  &__step-label {
    font-size: 11.5px;
    color: rgba(0, 0, 0, 0.5);
  }

  &__step-rate {
    font-size: 11px;
    font-weight: 600;
    color: #27a950;
    margin-top: 2px;
    min-height: 14px; /* leere Rate: Platz reservieren, Boxen bleiben gleich hoch */
    white-space: pre; /* damit " " als Platzhalter nicht kollabiert */
  }
}

@keyframes live-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(39, 169, 80, 0.5);
  }
  55% {
    box-shadow: 0 0 0 6px rgba(39, 169, 80, 0);
  }
}
</style>
